import { useState, useCallback } from 'react';
import { RegistroFisicoDto, CrearRegistroFisicoDto } from '../types/jugador';
import { jugadorService } from '../services/JugadorService';

/**
 * Hook para manejar registros físicos de jugadores
 */
export const useRegistroFisico = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Obtiene el último registro físico de un jugador
   * @param jugadorId - ID del jugador
   * @returns Promise con el último registro físico
   */
  const obtenerUltimoRegistro = useCallback(async (jugadorId: number): Promise<RegistroFisicoDto | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await jugadorService.getUltimoRegistroFisico(jugadorId);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        // Si no hay registros, no es un error, simplemente retornamos null
        if (response.message?.includes('no encontrado') || response.message?.includes('No hay registros')) {
          return null;
        }
        setError(response.message || 'Error al obtener el registro físico');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Crea un nuevo registro físico para un jugador
   * @param jugadorId - ID del jugador
   * @param registroData - Datos del registro físico
   * @returns Promise con el registro físico creado
   */
  const crearRegistro = useCallback(async (jugadorId: number, registroData: CrearRegistroFisicoDto): Promise<RegistroFisicoDto | null> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await jugadorService.crearRegistroFisico(jugadorId, registroData);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        setError(response.message || 'Error al crear el registro físico');
        return null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    obtenerUltimoRegistro,
    crearRegistro,
    clearError: () => setError(null)
  };
};